import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import CheckmarkCurvedIcon from "@src/lib/icons/12px/checkmark-curved";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import ChevronsOpenIcon from "@src/lib/icons/12px/chevrons-open";
import ChevronUpIcon from "@src/lib/icons/12px/chevron-up";

import { cn } from "@src/lib/utils/cn";
import { MENU_MOTION_CLASSNAME } from "@src/lib/ui/menuMotion";
import {
  MENU_ITEM_CLASSNAME,
  MENU_ITEM_INDICATOR_CLASSNAME,
  MENU_LABEL_CLASSNAME,
  MENU_SEPARATOR_CLASSNAME,
  MENU_SURFACE_CLASSNAME,
} from "@src/lib/ui/menuStyles";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

/**
 * The canonical input-like field trigger look. Use this (not `Button`) for
 * form-control triggers such as comboboxes and multi-selects, so they read
 * as fields and don't inherit action-button styling.
 *
 * Matches the Input field: 36px, rounded-lg, a border-secondary hairline on
 * bg-primary, 14px text, and Button's 3px brand-border focus ring (also shown
 * while the list is open). An empty Select (data-placeholder) shows its
 * placeholder in text-tertiary; aria-invalid switches border and ring to
 * status-error.
 */
export const selectTriggerClassName =
  "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-border-secondary bg-bg-primary px-3 py-2 font-sans-v4 text-s-v4 font-normal-v4 text-text-primary transition-[color,border-color,box-shadow] outline-none focus-visible:border-brand focus-visible:ring-[3px] focus-visible:ring-brand-border data-[state=open]:border-brand data-[state=open]:ring-[3px] data-[state=open]:ring-brand-border data-[placeholder]:text-text-tertiary disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-status-error-solid aria-invalid:focus-visible:border-status-error-solid aria-invalid:focus-visible:ring-status-error-border aria-invalid:data-[state=open]:border-status-error-solid aria-invalid:data-[state=open]:ring-status-error-border";

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, asChild, ...props }, ref) => {
  if (asChild) {
    return (
      <SelectPrimitive.Trigger
        ref={ref}
        asChild
        data-testid="select-trigger"
        className={className}
        {...props}
      >
        {children}
      </SelectPrimitive.Trigger>
    );
  }
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      data-testid="select-trigger"
      className={cn(selectTriggerClassName, "[&_span]:line-clamp-1", className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronsOpenIcon className="shrink-0 text-icon-secondary" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1 text-icon-secondary",
      className,
    )}
    {...props}
  >
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1 text-icon-secondary",
      className,
    )}
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    position?: "popper" | "fixed";
    portal?: boolean;
  }
>(
  (
    { className, children, position = "popper", portal = true, ...props },
    ref,
  ) => {
    const content = (
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-[9999] max-h-96",
          MENU_SURFACE_CLASSNAME,
          // The Viewport carries the p-1 so the scroll buttons stay flush.
          "p-0 origin-[var(--radix-select-content-transform-origin)]",
          MENU_MOTION_CLASSNAME,
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    );
    if (portal) {
      return <SelectPrimitive.Portal>{content}</SelectPrimitive.Portal>;
    }
    return content;
  },
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(MENU_LABEL_CLASSNAME, className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-testid="select-item"
    className={cn(
      MENU_ITEM_CLASSNAME,
      // The check sits in an 8px-inset slot on the right.
      "w-full pr-8",
      className,
    )}
    {...props}
  >
    <span className={cn(MENU_ITEM_INDICATOR_CLASSNAME, "right-2 left-auto")}>
      <SelectPrimitive.ItemIndicator>
        <CheckmarkCurvedIcon />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(MENU_SEPARATOR_CLASSNAME, className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
